import React, { useCallback } from "react";
import { Link, NavLink, useLocation, useParams, useNavigate } from "react-router-dom";

export const LOCALES = ["de", "fr", "it", "en"];
export const DEFAULT_LOCALE = "de";

export function detectBrowserLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const saved = localStorage.getItem("ch_lang");
  if (saved && LOCALES.includes(saved)) return saved;
  const nav = (navigator.language || DEFAULT_LOCALE).slice(0, 2).toLowerCase();
  return LOCALES.includes(nav) ? nav : DEFAULT_LOCALE;
}

/** Strip leading "/de" / "/fr" / "/it" / "/en" from a pathname. */
export function stripLocale(pathname) {
  const m = pathname.match(/^\/(de|fr|it|en)(\/|$)/);
  if (!m) return pathname;
  const rest = pathname.slice(m[0].length - (m[2] === "/" ? 1 : 0));
  return rest || "/";
}

/** Read the active locale from a pathname (or null if unprefixed). */
export function pathnameLocale(pathname) {
  const m = pathname.match(/^\/(de|fr|it|en)(\/|$)/);
  return m ? m[1] : null;
}

/** Hook: returns active locale (URL > localStorage > browser). */
export function useActiveLocale() {
  const { locale: param } = useParams();
  const { pathname } = useLocation();
  if (param && LOCALES.includes(param)) return param;
  return pathnameLocale(pathname) || detectBrowserLocale();
}

/** Build a localized URL given a path that may be unprefixed (e.g. "/listings"). */
export function buildPath(locale, path) {
  if (!path) path = "/";
  if (typeof path !== "string") return path; // location-objects pass through
  // strip an existing locale prefix (so callers can pass "/de/listings" or "/listings")
  const cleaned = stripLocale(path);
  if (cleaned === "/") return `/${locale}`;
  return `/${locale}${cleaned.startsWith("/") ? cleaned : "/" + cleaned}`;
}

/** Drop-in Link that auto-prefixes the active locale. */
export function L({ to, ...rest }) {
  const locale = useActiveLocale();
  return <Link to={buildPath(locale, to)} {...rest} />;
}

/** Drop-in NavLink that auto-prefixes the active locale. */
export function LN({ to, ...rest }) {
  const locale = useActiveLocale();
  return <NavLink to={buildPath(locale, to)} {...rest} />;
}

/**
 * Hook returning a navigate fn that auto-prefixes the current locale for
 * string paths. Object location (e.g. { pathname }) is passed through unchanged.
 * Returns a STABLE callback so consumers can safely include it in useEffect
 * dependency arrays without causing infinite re-runs.
 */
export function useLocaleNavigate() {
  const navigate = useNavigate();
  const locale = useActiveLocale();
  return useCallback((to, options) => {
    if (typeof to === "string") return navigate(buildPath(locale, to), options);
    if (typeof to === "number") return navigate(to);
    return navigate(to, options);
  }, [navigate, locale]);
}
